import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';
import {
  Dimensions,
  PanResponder,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import useVehicleCostCalculator from '../../hooks/useVehicleCostCalculator';
import useFuelPrices from '../../hooks/useFuelPrices';
import { FUEL_PRICE_CONFIG } from '../../utils/formatters';
import logger from '../../utils/logger';

const ProductSide = ({
  calculatorInputData = {
    estimated_price_range: '$0 - $0',
    estimated_annual_depreciation_percentage: 0,
    fuel_consumption_l_100km_combined: 0,
    monthly_ownership_cost: 0,
  },
  region = 'EU',
}) => {
  const posthog = usePostHog();
  const { fuelPrices: cachedFuelPrices, isLoading: fuelPricesLoading } = useFuelPrices();

  // Get regional fuel price config
  const fuelConfig = FUEL_PRICE_CONFIG[region] || FUEL_PRICE_CONFIG.EU;
  const currencySymbol = fuelConfig.symbol;

  // Use cached fuel price as default for UK, fall back to hardcoded config default
  const defaultFuelCost = (!fuelPricesLoading && cachedFuelPrices && region === 'UK')
    ? cachedFuelPrices.petrol
    : fuelConfig.default;

  const [fuelCost, setFuelCost] = useState(defaultFuelCost);
  const hasSetFromCache = useRef(false);

  // Update default when cached prices arrive (only once)
  useEffect(() => {
    if (!fuelPricesLoading && cachedFuelPrices && region === 'UK' && !hasSetFromCache.current) {
      hasSetFromCache.current = true;
      setFuelCost(cachedFuelPrices.petrol);
    }
  }, [fuelPricesLoading, cachedFuelPrices, region]);
  const [travelDistance, setTravelDistance] = useState(region === 'EU' ? 245 : 150); // km for EU, miles for UK/US

  // Track screen width for responsive layout
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const BREAKPOINT_WIDTH = 768;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });
    return () => subscription?.remove();
  }, []);

  const isMobile = screenWidth < BREAKPOINT_WIDTH;

  // Calculate slider width based on screen size
  const sliderWidth = isMobile ? screenWidth - 80 : 364; // Full width minus padding on mobile

  // Use regional fuel price bounds
  const minCost = fuelConfig.min;
  const maxCost = fuelConfig.max;

  // Distance unit based on region
  const distanceUnit = region === 'EU' ? 'km' : 'miles';

  // Merge calculator input data with user inputs
  // Onboarding data is now passed via calculatorInputData from ProductDetailContext
  // Calculator expects normalized km - convert miles to km for UK/US
  const distanceInKm = region === 'EU' ? travelDistance : travelDistance * 1.60934;
  // Note: userInput_fuel_price_per_liter is actually per gallon for US (confusing name)
  // The calculator hook handles the unit difference internally

  const calculatorData = {
    ...calculatorInputData,
    userInput_monthly_distance_km: distanceInKm,
    userInput_fuel_price_per_liter: fuelCost, // per liter for EU/UK, per gallon for US
    region: region, // Pass region to calculator for correct formula selection
  };

  // Use the calculator hook to get real-time cost calculations
  const {
    totalCost,
    // depreciationCost,
    fuelCost: fuelCostMonthly,
    maintenanceCost,
    isValid,
    warnings,
  } = useVehicleCostCalculator(calculatorData);

  // Log warnings in development (only if we have valid data)
  // Don't log warnings during initial render when data is still loading
  const hasValidData =
    calculatorInputData.estimated_price_range && calculatorInputData.estimated_price_range !== '';
  if (__DEV__ && warnings.length > 0 && hasValidData) {
    logger.warn('Vehicle Cost Calculator Warnings:', warnings);
  }

  // Calculate derived values for display (keep as numbers for accuracy)
  const totalRunningCost = fuelCostMonthly + maintenanceCost;

  // Calculate slider thumb position based on fuel cost
  const getThumbPosition = () => {
    const percentage = (fuelCost - minCost) / (maxCost - minCost);
    return percentage * (sliderWidth - 16); // Subtract thumb width
  };

  // Pan responder for slider
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (evt) => {
        updateSliderValue(evt.nativeEvent.locationX);
      },
      onPanResponderMove: (evt) => {
        updateSliderValue(evt.nativeEvent.locationX);
      },
    })
  ).current;

  const updateSliderValue = (locationX) => {
    const clampedX = Math.max(0, Math.min(locationX, sliderWidth));
    const percentage = clampedX / sliderWidth;
    const rawValue = minCost + percentage * (maxCost - minCost);
    // Round to nearest 0.05
    const roundedValue = Math.round(rawValue / 0.05) * 0.05;
    setFuelCost(Math.max(minCost, Math.min(maxCost, roundedValue)));
  };

  return (
    <View style={[styles.container, isMobile && styles.containerMobile]}>
      {/* Monthly Costs Calculator */}
      <View style={[styles.calculatorCard, isMobile && styles.calculatorCardMobile]}>
        <Text style={styles.cardTitle}>Monthly costs calculator</Text>
        <View style={[styles.calculatorContent, isMobile && styles.calculatorContentMobile]}>
          {/* Fuel Cost Slider */}
          <View style={styles.inputRow}>
            <Text style={styles.inputLabel}>Cost {fuelConfig.unit}</Text>
            <Text style={styles.inputValue}>{currencySymbol}{fuelCost.toFixed(2)}</Text>
          </View>
          <View
            style={[styles.sliderContainer, isMobile && styles.sliderContainerMobile]}
            {...panResponder.panHandlers}
          >
            <View style={[styles.sliderTrack, isMobile && styles.sliderTrackMobile]} />
            <View style={[styles.sliderThumb, { left: getThumbPosition() }]} />
          </View>

          {/* Travel Distance Input */}
          <View style={styles.distanceRow}>
            <Text style={styles.inputLabel}>Travel distance</Text>
            <View style={styles.distanceInput}>
              <TextInput
                style={styles.distanceValue}
                value={String(travelDistance)}
                onChangeText={(text) => {
                  const numValue = parseInt(text.replace(/[^0-9]/g, ''), 10);
                  if (!Number.isNaN(numValue)) {
                    setTravelDistance(numValue);
                  } else if (text === '') {
                    setTravelDistance(0);
                  }
                }}
                keyboardType="numeric"
                maxLength={5}
              />
              <Text style={styles.distanceUnit}>{distanceUnit}</Text>
            </View>
          </View>

          {/* Cost Breakdown */}
          <View style={styles.costBreakdown}>
            {/* Individual running cost items */}
            <View style={styles.costRow}>
              <Text style={styles.costLabel}>Fuel</Text>
              <Text style={styles.costValue}>{currencySymbol}{fuelCostMonthly.toFixed(2)}</Text>
            </View>
            <View style={styles.costRow}>
              <Text style={styles.costLabel}>Maintenance</Text>
              <Text style={styles.costValue}>{currencySymbol}{maintenanceCost.toFixed(2)}</Text>
            </View>

            {/* Divider and running cost total */}
            <View style={[styles.costDivider, isMobile && styles.costDividerMobile]} />
            <View style={styles.costRow}>
              <Text style={styles.costLabelTotal}>Total running cost</Text>
              <Text style={styles.costValueTotal}>{currencySymbol}{totalRunningCost.toFixed(2)}</Text>
            </View>

            {/* Disclaimer */}
            <Text style={styles.disclaimerText}>Insurance and taxes not included</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 412,
    flexDirection: 'column',
    gap: 24,
  },
  containerMobile: {
    width: '100%',
  },
  cardTitle: {
    position: 'absolute',
    top: 24,
    left: 24,
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    lineHeight: 16 * 1.4,
    color: '#FFFFFF',
  },
  // Calculator Card Styles
  calculatorCard: {
    height: 412,
    backgroundColor: '#1D242F',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: 16,
    position: 'relative',
  },
  calculatorCardMobile: {
    height: 'auto',
    minHeight: 380,
  },
  calculatorContent: {
    position: 'absolute',
    top: 78,
    left: 24,
    width: 364,
    flexDirection: 'column',
    gap: 16,
  },
  calculatorContentMobile: {
    position: 'relative',
    width: '100%',
    left: 0,
    top: 0,
    paddingTop: 78,
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  inputRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  inputLabel: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    lineHeight: 14 * 1.4,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  inputValue: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    lineHeight: 14 * 1.4,
    color: '#FFFFFF',
  },
  sliderContainer: {
    width: 364,
    height: 16,
    position: 'relative',
  },
  sliderContainerMobile: {
    width: '100%',
  },
  sliderTrack: {
    position: 'absolute',
    top: 7,
    left: 0,
    width: 364,
    height: 3,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 99,
  },
  sliderTrackMobile: {
    width: '100%',
  },
  sliderThumb: {
    position: 'absolute',
    top: 0,
    width: 16,
    height: 16,
    backgroundColor: '#0556F7',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#FFFFFF',
    boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.4)',
  },
  distanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  distanceInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: 96,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  distanceValue: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    lineHeight: 14 * 1.4,
    color: '#FFFFFF',
    padding: 0,
    margin: 0,
    textAlign: 'left',
    minWidth: 40,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  distanceUnit: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    lineHeight: 14 * 1.4,
    color: 'rgba(255, 255, 255, 0.4)',
  },
  costBreakdown: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 16,
    gap: 12,
  },
  costRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  costLabel: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    lineHeight: 14 * 1.4,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  costValue: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    lineHeight: 14 * 1.4,
    color: '#FFFFFF',
  },
  costDivider: {
    width: 364 - 32,
    height: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  costDividerMobile: {
    width: '100%',
  },
  costLabelTotal: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    lineHeight: 14 * 1.4,
    color: '#FFFFFF',
  },
  costValueTotal: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    lineHeight: 14 * 1.4,
    color: '#FFFFFF',
  },
  disclaimerText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    lineHeight: 12 * 1.4,
    color: 'rgba(255, 255, 255, 0.4)',
    textAlign: 'center',
    marginTop: 4,
  },
});

export default ProductSide;
